import { useState } from 'react';
import { Box, Card, CardContent, Modal, Tab, Tabs } from '@mui/material';
import { User } from 'src/models/user';
import UserMembershipHistory from './UserMembershipHistory';
import { TabPanel } from 'src/components/shared/TabPanel';
import UserMembership from './UserMembership';
import UserProfessionalAccess from '../professionalAccess/UserProfessionalAccess';
import UserOrganization from '../professionalAccess/UserOrganization';
import { modalStyles } from 'src/pages/dashboard/UsersDetailsLookup';
import { Membership } from 'src/models/membership';
import { ROLES, useAuthState } from 'src/contexts/authContext';

type Props = {
  loading: boolean;
  user: User;

  pro?: boolean;
  handleClickInvoiceRefund: (id: string, provider: string) => void;
  refundInvoiceLoading: boolean;
  userLoading: boolean;
  refreshUser: (email: string) => void;
};

const UserMembershipsCard = ({
  loading,
  user,
  pro,
  handleClickInvoiceRefund,
  refundInvoiceLoading,
  refreshUser
}: Props) => {
  const authState = useAuthState();

  // Professional management (feature access + organization) is restricted to
  // Developer Admin, CSA Admin and CSA Manager (SEN-6029). minAuthLevelMet
  // covers CSA_MANAGER and above; DEV_ADMIN is not in the level map, so check it
  // explicitly. Only shown on the professional card.
  const canManageProfessional =
    !!pro &&
    (authState.minAuthLevelMet(ROLES.CSA_MANAGER) ||
      authState.isRole(ROLES.DEV_ADMIN));

  const [tabValue, setTabValue] = useState<number>(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [selected, setSelected] = useState<Membership | undefined>();
  const [expiredMembershipModalOpen, setExpiredMembershipModalOpen] =
    useState(false);

  const membershipModalStyles = {
    ...modalStyles,
    overflow: 'hidden'
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',

          button: {
            textTransform: 'none'
            // fontSize: '0.9rem',
            // color: '#cccccc',
            // '&.Mui-selected': {
            //   color: '#FFFFFF'
            // }
          },
          '&.MuiTabs-indicator': {
            backgroundColor: '#084656'
          }
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              pro
                ? 'Professional Subscription'
                : `${user.membership?.shortName ?? 'Personal'} Membership`
            }
          />
          <Tab label={pro ? 'Subscription History' : 'History'} />
          {canManageProfessional && <Tab label="Feature Access" />}
          {canManageProfessional && <Tab label="Organization" />}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <CardContent>
          <UserMembership
            user={user}
            pro={pro}
            loading={loading}
            handleClickInvoiceRefund={handleClickInvoiceRefund}
            refundInvoiceLoading={refundInvoiceLoading}
            userLoading={loading}
            refreshUser={refreshUser}
          />
        </CardContent>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <UserMembershipHistory
          memberships={
            pro ? user.expiredProMemberships : user.expiredMemberships
          }
          setSelectedMembership={(membership: Membership) => {
            setSelected(membership);
            setExpiredMembershipModalOpen(true);
          }}
        />
      </TabPanel>
      {canManageProfessional && (
        <TabPanel value={tabValue} index={2}>
          <UserProfessionalAccess user={user} refreshUser={refreshUser} />
        </TabPanel>
      )}
      {canManageProfessional && (
        <TabPanel value={tabValue} index={3}>
          <UserOrganization user={user} refreshUser={refreshUser} />
        </TabPanel>
      )}
      <Modal
        open={expiredMembershipModalOpen}
        onClose={() => setExpiredMembershipModalOpen(false)}
        sx={membershipModalStyles}
      >
        <Card sx={{ maxHeight: '100%', overflow: 'auto' }}>
          <CardContent>
            <UserMembership
              user={user}
              pro={pro}
              displayMembership={selected}
              loading={loading}
              handleClickInvoiceRefund={handleClickInvoiceRefund}
              refundInvoiceLoading={refundInvoiceLoading}
              userLoading={loading}
              refreshUser={refreshUser}
            />
          </CardContent>
        </Card>
      </Modal>
    </Box>
  );
};

export default UserMembershipsCard;
